import { ampli, type Event, type EventOptions } from './ampli';

/**
 * Ampli's generated client (`./ampli`) silently drops any typed event call made
 * before `ampli.load()` has run — `Ampli.track()` checks a private `this.amplitude` field set
 * only inside `load()`, and short-circuits to a no-op if it's unset (console.error, no send, no
 * retry, no internal queue).
 *
 * This is a gap relative to the underlying `@amplitude/analytics-browser` SDK, which does queue
 * `track()` calls made before `init()` and flushes them once it runs — the pattern Amplitude's
 * own docs recommend for consent-gated tracking. The generated client doesn't inherit that
 * behavior, so this module patches `ampli.track()` once to add it back: every generated event
 * method (`recommendationsImpression`, `navigationAction`, etc.) routes through this same
 * `track()` call, so patching it here covers all of them without touching each call site.
 */
type TrackArgs = [event: Event, options?: EventOptions];

// Bounds worst-case memory for sessions where ampli.load() never runs at all (denied consent,
// ad blockers, NEXT_PUBLIC_USE_AMPLITUDE off) — queued calls just sit unsent until then.
const MAX_QUEUED_CALLS = 50;

let queuedCalls: TrackArgs[] = [];
const originalTrack = ampli.track.bind(ampli);

ampli.track = (...args: TrackArgs) => {
  if (!ampli.isLoaded && queuedCalls.length < MAX_QUEUED_CALLS) {
    queuedCalls.push(args);
  }
  return originalTrack(...args);
};

// Called once from initAmplitudeAnalytics, right after ampli.load(), so calls made before consent
// resolved still reach Amplitude instead of being lost.
export const flushQueuedAmpliCalls = () => {
  const calls = queuedCalls;
  queuedCalls = [];
  calls.forEach((args) => originalTrack(...args));
};
